/**
 * Who else has this customer open right now (ADR 0268).
 *
 * Two reps on the same customer at once used to find out when the customer
 * said "your colleague already called". Every job and customer screen now
 * announces itself on a presence channel named for the customer, and listens
 * for anyone else on it. Nothing is written to the database: presence is
 * held by Supabase Realtime for exactly as long as the tab is open and
 * connected, and vanishes on its own when it closes or loses its connection.
 * That is the right lifetime for "working on it now" — a heartbeat row in a
 * table would need a sweeper, and would go on saying a rep was on a customer
 * for minutes after they locked their phone.
 *
 * The channel is per customer, not per job, because that is who gets stepped
 * on: the customer with three jobs is one person to call, and the rep on the
 * contact file is as much "on" them as the rep inside a job.
 */

/** What one open tab says about itself. */
export interface WorkingOnPayload {
  profileId: string;
  name: string;
  /** The job that tab has open, or null from the customer file. */
  jobId: string | null;
  jobNumber: number | null;
  /** ISO, when that tab opened the customer. */
  since: string;
}

/** One person on the customer — their earliest tab, if they have several. */
export interface Worker extends WorkingOnPayload {
  /**
   * When each of their open tabs opened the customer, earliest first. Their
   * stay lasts while any one of them is open, so closing the first tab is not
   * leaving and opening the second was not arriving.
   */
  tabsSince?: string[];
}

type PresenceState = Record<
  string,
  (WorkingOnPayload & { presence_ref?: string })[]
>;

/**
 * Everybody on the customer who is not me, one entry per person.
 *
 * The same rep on a phone and a laptop is one rep, and a rep is never warned
 * about themselves — their other tab is not someone to check with.
 */
export function othersOn(state: PresenceState, myProfileId: string): Worker[] {
  const byPerson = new Map<string, Worker>();
  for (const entries of Object.values(state)) {
    for (const entry of entries) {
      if (!entry || typeof entry.profileId !== "string") continue;
      if (entry.profileId === myProfileId) continue;
      const seen = byPerson.get(entry.profileId);
      const tabsSince = [...(seen?.tabsSince ?? []), entry.since].sort();
      if (!seen || entry.since < seen.since) {
        byPerson.set(entry.profileId, {
          profileId: entry.profileId,
          name: entry.name,
          jobId: entry.jobId ?? null,
          jobNumber: entry.jobNumber ?? null,
          since: entry.since,
          tabsSince,
        });
      } else {
        seen.tabsSince = tabsSince;
      }
    }
  }
  return [...byPerson.values()].sort((a, b) => a.since.localeCompare(b.since));
}

export const workingOnTopic = (contactId: string) => `working-on:${contactId}`;
